//frontend/dispatcher-client/src/hooks/usePersonnelCompensationData.js
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  fetchPersonnelPeople,
  fetchPersonnelPublicHolidays,
  fetchPersonnelHolidayWork,
} from "../api/personnelApi";

import {
  loadPersonnelResource,
  readPersonnelCache,
  subscribePersonnelCache,
} from "../utils/personnelCache";

import {
  getPersonnelYearRanges,
} from "../lib/prefetchPersonnel";

/* =========================================================
 * CACHE
 * ======================================================= */

const readCompensationCache =
  (
    year
  ) => {
    const ranges =
      getPersonnelYearRanges(
        year
      );

    return {
      ranges,

      people:
        readPersonnelCache(
          "people"
        ),

      publicHolidays:
        readPersonnelCache(
          "publicHolidays",
          ranges.publicHolidays
        ),

      holidayWork:
        readPersonnelCache(
          "holidayWork",
          ranges.holidayWork
        ),
    };
  };

/* =========================================================
 * HOOK
 * ======================================================= */

export default function usePersonnelCompensationData(
  year
) {
  const initial =
    readCompensationCache(
      year
    );

  const [
    people,
    setPeople,
  ] =
    useState(
      () =>
        initial.people
          ?.data ||
        []
    );

  const [
    publicHolidays,
    setPublicHolidays,
  ] =
    useState(
      () =>
        initial.publicHolidays
          ?.data ||
        []
    );

  const [
    holidayWork,
    setHolidayWork,
  ] =
    useState(
      () =>
        initial.holidayWork
          ?.data ||
        []
    );

  const [
    loading,
    setLoading,
  ] =
    useState(
      () =>
        !(
          initial.people &&
          initial.publicHolidays &&
          initial.holidayWork
        )
    );

  const [
    refreshing,
    setRefreshing,
  ] =
    useState(
      false
    );

  const [
    error,
    setError,
  ] =
    useState(
      null
    );

  const requestRef =
    useRef(
      0
    );

  const realtimeRefreshTimerRef =
    useRef(
      null
    );

  /* =======================================================
   * REFRESH
   * ===================================================== */

  const refresh =
    useCallback(
      async ({
        silent =
          true,
      } = {}) => {
        const requestId =
          requestRef.current +
          1;

        requestRef.current =
          requestId;

        const ranges =
          getPersonnelYearRanges(
            year
          );

        if (!silent) {
          setLoading(
            true
          );
        }

        setRefreshing(
          true
        );

        setError(
          null
        );

        const results =
          await Promise.allSettled([
            loadPersonnelResource({
              resource:
                "people",

              params: {},

              force:
                true,

              loader:
                () =>
                  fetchPersonnelPeople(),
            }),

            loadPersonnelResource({
              resource:
                "publicHolidays",

              params:
                ranges.publicHolidays,

              force:
                true,

              loader:
                () =>
                  fetchPersonnelPublicHolidays(
                    ranges.publicHolidays
                  ),
            }),

            loadPersonnelResource({
              resource:
                "holidayWork",

              params:
                ranges.holidayWork,

              force:
                true,

              loader:
                () =>
                  fetchPersonnelHolidayWork(
                    ranges.holidayWork
                  ),
            }),
          ]);

        if (
          requestRef.current !==
          requestId
        ) {
          return;
        }

        const [
          peopleResult,
          holidaysResult,
          holidayWorkResult,
        ] =
          results;

        let failures =
          0;

        if (
          peopleResult.status ===
          "fulfilled"
        ) {
          setPeople(
            peopleResult.value
          );
        } else {
          failures += 1;
        }

        if (
          holidaysResult.status ===
          "fulfilled"
        ) {
          setPublicHolidays(
            holidaysResult.value
          );
        } else {
          failures += 1;
        }

        if (
          holidayWorkResult.status ===
          "fulfilled"
        ) {
          setHolidayWork(
            holidayWorkResult.value
          );
        } else {
          failures += 1;
        }

        if (
          failures > 0
        ) {
          const refreshError =
            new Error(
              "Some Personnel compensation data could not be refreshed."
            );

          console.error(
            "[PersonnelCompensation] refresh failed:",
            refreshError
          );

          setError(
            refreshError
          );
        }

        setLoading(
          false
        );

        setRefreshing(
          false
        );
      },
      [
        year,
      ]
    );

  /* =======================================================
   * YEAR CHANGE / INITIAL LOAD
   * ===================================================== */

  useEffect(
    () => {
      const cached =
        readCompensationCache(
          year
        );

      setPeople(
        cached.people
          ?.data ||
        []
      );

      setPublicHolidays(
        cached.publicHolidays
          ?.data ||
        []
      );

      setHolidayWork(
        cached.holidayWork
          ?.data ||
        []
      );

      const hasAllCache =
        Boolean(
          cached.people &&
          cached.publicHolidays &&
          cached.holidayWork
        );

      const hasStaleCache =
        Boolean(
          cached.people
            ?.isStale ||
          cached.publicHolidays
            ?.isStale ||
          cached.holidayWork
            ?.isStale
        );

      setLoading(
        !hasAllCache
      );

      /*
       * Fresh complete cache:
       * render instantly and do nothing.
       *
       * Stale complete cache:
       * render instantly + silent revalidation.
       *
       * Missing cache:
       * normal initial load.
       */
      if (
        !hasAllCache ||
        hasStaleCache
      ) {
        refresh({
          silent:
            hasAllCache,
        });
      } else {
        setRefreshing(
          false
        );

        setError(
          null
        );
      }
    },
    [
      year,
      refresh,
    ]
  );

  /* =======================================================
   * CACHE SYNC + REALTIME
   * ===================================================== */

  useEffect(
    () => {
      const unsubscribe =
        subscribePersonnelCache(
          (
            event
          ) => {
            if (
              ![
                "people",
                "publicHolidays",
                "holidayWork",
              ].includes(
                event.resource
              )
            ) {
              return;
            }

            /*
             * A realtime mutation marks one or more
             * resources stale.
             *
             * Several stale events may arrive together
             * for the same backend mutation, so coalesce
             * them into one silent refresh cycle.
             */
            if (
              event.stale ===
              true
            ) {
              const requestVersionAtEvent =
                requestRef.current;

              if (
                realtimeRefreshTimerRef.current
              ) {
                window.clearTimeout(
                  realtimeRefreshTimerRef.current
                );
              }

              realtimeRefreshTimerRef.current =
                window.setTimeout(
                  () => {
                    realtimeRefreshTimerRef.current =
                      null;

                    /*
                     * A normal page refresh may already
                     * have started after the socket event.
                     *
                     * If so, do not launch another one.
                     */
                    if (
                      requestRef.current !==
                      requestVersionAtEvent
                    ) {
                      return;
                    }

                    refresh({
                      silent:
                        true,
                    }).catch(
                      () => {
                        /*
                         * refresh() owns the error state.
                         */
                      }
                    );
                  },
                  60
                );

              return;
            }

            /*
             * Normal cache writes:
             * synchronise state immediately without
             * another HTTP request.
             */
            const cached =
              readCompensationCache(
                year
              );

            if (
              cached.people
            ) {
              setPeople(
                cached.people.data
              );
            }

            if (
              cached.publicHolidays
            ) {
              setPublicHolidays(
                cached.publicHolidays.data
              );
            }

            if (
              cached.holidayWork
            ) {
              setHolidayWork(
                cached.holidayWork.data
              );
            }
          }
        );

      return () => {
        unsubscribe();

        if (
          realtimeRefreshTimerRef.current
        ) {
          window.clearTimeout(
            realtimeRefreshTimerRef.current
          );

          realtimeRefreshTimerRef.current =
            null;
        }
      };
    },
    [
      year,
      refresh,
    ]
  );

  return {
    people,
    publicHolidays,
    holidayWork,

    loading,
    refreshing,
    error,

    refresh,
  };
}